import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import MainCheckbox from '../../../components/MUI/MainCheckbox/MainCheckbox';
import Validation from '../../../components/Validation/Validation';
import MainButton from '../../../components/MUI/MainButton';
import AttachedCard from '../../../components/Confirmation/PageContent/AttachedCard/AttachedCard';
import useDeviceResize from '../../../hooks/useDeviceResize';
import PolicySummaryWrapper from '../PolicySummaryWrapper';
import { ATTACH_PAYMENT_CARD_OPTIONS } from '../../../constants';
import AutomaticRecurringInfoBlock from '../../../components/AutomaticRecurringInfoBlock';

import useStyles from '../PolicySummaryWrapper/makeStyles';
import attachIcon from '../../../img/svgs/payment/attach.svg';

const PolicySummary = ({
  openMobileSummary,
  onDismiss,
  handlePayClick,
  attachCardAgreement,
  setAttachCardAgreement,
  basket,
  currentStep,
  formProps,
  contract,
}) => {
  const { t } = useTranslation();
  const isDeviceLgSize = useDeviceResize('lg');
  const classes = useStyles();
  const { errors } = formProps;

  const handleCheckboxChange = (event) => {
    const { checked } = event.target;
    setAttachCardAgreement(checked);
  };

  const isDisabled = useMemo(
    () => (
      basket.length === 0 || currentStep.id === ATTACH_PAYMENT_CARD_OPTIONS.ATTACH_CARD
    ) || (!isEmpty(contract.attachedCard) || contract.isFullyPaid) || false,
    [basket, currentStep, contract],
  );

  const renderDesktopView = () => (
    <>
      <AutomaticRecurringInfoBlock
        isDisabled={isDisabled}
        checked={attachCardAgreement}
        errors={errors}
        handleCheckboxChange={handleCheckboxChange}
      />
      <Grid item xs={4} className={classes.sectionBox}>
        <Grid item className={cn(classes.sectionRow, classes.unsetPaddingLeft)}>
          {
            !isEmpty(contract.attachedCard) ? (
              <div className={classes.attachedWrapper}>
                <AttachedCard attachedCard={contract.attachedCard} />
              </div>
            ) : (
              <MainButton
                variant="contained"
                color="primary"
                onClick={handlePayClick}
                className={classes.buyButton}
                width="fullWidth"
                startIcon={<img src={attachIcon} alt="cardIcon" />}
                disabled={!attachCardAgreement || basket.length === 0 || contract.isFullyPaid}
              >
                {t('automaticRecurringPayment.attachCard')}
              </MainButton>
            )
          }
        </Grid>
      </Grid>
    </>
  );

  const renderMobileView = () => (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={11}>
        <Validation name="attachCardAgreement" errors={errors}>
          <div className={classes.mobileSummaryCheckbox}>
            <label className={cn({ [classes.agreementLabelDisabled]: isDisabled })} htmlFor="attachCardAgreement">
              <MainCheckbox
                checked={attachCardAgreement}
                onChange={handleCheckboxChange}
                color="primary"
                style={{ marginRight: '0.5rem' }}
                disabled={isDisabled}
              />
            </label>
            <div
              className={cn(classes.agreementTextMobile, {
                [classes.agreementLabelDisabled]: isDisabled,
              })}
            >
              <p dangerouslySetInnerHTML={{
                __html: t('automaticRecurringPayment.agreementText'),
              }}
              />
            </div>
          </div>
        </Validation>
      </Grid>
    </Grid>
  );

  return (
    <PolicySummaryWrapper openMobileSummary={openMobileSummary} onDismiss={onDismiss}>
      <div className={classes.policySummaryContainer}>
        {isDeviceLgSize ? renderMobileView() : renderDesktopView()}
      </div>
    </PolicySummaryWrapper>
  );
};

PolicySummary.propTypes = {
  openMobileSummary: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  handlePayClick: PropTypes.func,
  attachCardAgreement: PropTypes.bool,
  setAttachCardAgreement: PropTypes.func.isRequired,
  basket: PropTypes.arrayOf(PropTypes.shape({})),
  currentStep: PropTypes.shape({
    id: PropTypes.string,
  }),
  contract: PropTypes.shape({
    attachedCard: PropTypes.shape({}),
    isFullyPaid: PropTypes.bool,
  }),
  formProps: PropTypes.shape({
    errors: PropTypes.shape({
      attachCardAgreement: PropTypes.shape({}),
    }),
    setValue: PropTypes.func,
  }),
};

PolicySummary.defaultProps = {
  openMobileSummary: false,
  handlePayClick: () => { },
  attachCardAgreement: false,
  currentStep: {},
  basket: [],
  contract: {},
  formProps: {},
};

export default PolicySummary;
